import Supercluster from 'supercluster'
import type { Property } from '@/types/models'

/** Aligned with iOS MapView adaptive pin rendering. */
export const MAP_SEARCH_MIN_ZOOM = 14
export const MAP_PIN_HARD_BUDGET = 2000
export const MAP_CLUSTER_MIN_COUNT = 40
/** Soft cap on simultaneous marker nodes after clustering. */
export const MAP_SOFT_RENDER_BUDGET = 800

export type PinFeatureProps = {
  /** Primary property id (first in the location group). */
  propertyId: string
  propertyIds: string[]
  /** Units at this exact coordinate (≥1). */
  count: number
  label: string
  hasActiveBiddles: boolean
}

export type ClusterAggProps = {
  hot: number
  /** Total property units under this cluster (not just location points). */
  units: number
}

export type PinFeature = Supercluster.PointFeature<PinFeatureProps>
export type PinIndex = Supercluster<PinFeatureProps, ClusterAggProps>

export type MapLatLngBounds = {
  south: number
  west: number
  north: number
  east: number
}

/** Same precision as MapPage / iOS location grouping. */
export function locationCoordKey(lat: number, lng: number) {
  return `${lat.toFixed(8)},${lng.toFixed(8)}`
}

/** Keep only pins inside the viewport rectangle (geohash cells may over-fetch). */
export function filterPinsInViewportBounds(
  properties: Property[],
  bounds: MapLatLngBounds,
): Property[] {
  const { south, west, north, east } = bounds
  // Normal case: west < east. Antimeridian-spanning views are rare for AU use.
  if (west <= east) {
    return properties.filter((p) => {
      const lat = p.location.latitude
      const lng = p.location.longitude
      return lat >= south && lat <= north && lng >= west && lng <= east
    })
  }
  return properties.filter((p) => {
    const lat = p.location.latitude
    const lng = p.location.longitude
    return lat >= south && lat <= north && (lng >= west || lng <= east)
  })
}

export function budgetPinsNearestCenter(
  properties: Property[],
  center: { lat: number; lng: number },
  budget = MAP_PIN_HARD_BUDGET,
): { kept: Property[]; totalAvailable: number; truncated: boolean } {
  const totalAvailable = properties.length
  if (totalAvailable <= budget) {
    return { kept: properties, totalAvailable, truncated: false }
  }
  const scored = properties.map((p) => {
    const dLat = p.location.latitude - center.lat
    const dLng = p.location.longitude - center.lng
    return { p, dist2: dLat * dLat + dLng * dLng }
  })
  scored.sort((a, b) => a.dist2 - b.dist2)
  return {
    kept: scored.slice(0, budget).map((x) => x.p),
    totalAvailable,
    truncated: true,
  }
}

/**
 * Area-search display set: viewport filter first, then hard nearest-to-centre budget.
 * `totalAvailable` / truncation refer to in-viewport pins only (not geohash over-fetch).
 */
export function prepareAreaSearchPins(
  properties: Property[],
  bounds: MapLatLngBounds,
  center: { lat: number; lng: number },
  budget = MAP_PIN_HARD_BUDGET,
): { kept: Property[]; totalAvailable: number; truncated: boolean } {
  const inView = filterPinsInViewportBounds(properties, bounds)
  return budgetPinsNearestCenter(inView, center, budget)
}

/**
 * One Supercluster point per exact coordinate (multi-unit buildings collapse here).
 * Matches iOS MapPinRendering: same-location units stay a leaf group with a count,
 * not N stacked single pins / a zoom-cluster of identical points.
 */
export function propertiesToFeatures(properties: Property[]): PinFeature[] {
  type Group = {
    lat: number
    lng: number
    properties: Property[]
  }
  const groups = new Map<string, Group>()
  for (const p of properties) {
    const lat = p.location.latitude
    const lng = p.location.longitude
    if ((!lat && !lng) || (lat === 0 && lng === 0)) continue
    const key = locationCoordKey(lat, lng)
    const existing = groups.get(key)
    if (existing) existing.properties.push(p)
    else groups.set(key, { lat, lng, properties: [p] })
  }

  const out: PinFeature[] = []
  for (const group of groups.values()) {
    const first = group.properties[0]
    out.push({
      type: 'Feature',
      properties: {
        propertyId: first.id,
        propertyIds: group.properties.map((p) => p.id),
        count: group.properties.length,
        label:
          group.properties.length > 1
            ? `${group.properties.length} properties at this location`
            : first.fullAddress,
        hasActiveBiddles: group.properties.some((p) => p.hasActiveBiddles),
      },
      geometry: {
        type: 'Point',
        coordinates: [group.lng, group.lat],
      },
    })
  }
  return out
}

export function buildPinIndex(properties: Property[]): PinIndex {
  const index = new Supercluster<PinFeatureProps, ClusterAggProps>({
    radius: 60,
    maxZoom: 18,
    minPoints: 2,
    map: (props) => ({
      hot: props.hasActiveBiddles ? 1 : 0,
      units: props.count,
    }),
    reduce: (acc, props) => {
      acc.hot += props.hot
      acc.units += props.units
    },
  })
  index.load(propertiesToFeatures(properties))
  return index
}

export type VisibleCluster =
  | {
      kind: 'cluster'
      id: number
      lat: number
      lng: number
      count: number
      hot: boolean
    }
  | {
      kind: 'leaf'
      propertyId: string
      propertyIds: string[]
      count: number
      lat: number
      lng: number
      label: string
      hasActiveBiddles: boolean
    }

export function queryVisibleClusters(
  index: PinIndex,
  bounds: { south: number; west: number; north: number; east: number },
  zoom: number,
): VisibleCluster[] {
  const bbox: [number, number, number, number] = [
    bounds.west,
    bounds.south,
    bounds.east,
    bounds.north,
  ]
  const z = Math.max(0, Math.min(20, Math.floor(zoom)))
  const features = index.getClusters(bbox, z)
  const out: VisibleCluster[] = []
  for (const f of features) {
    const [lng, lat] = f.geometry.coordinates
    const props = f.properties
    if ('cluster' in props && props.cluster) {
      out.push({
        kind: 'cluster',
        id: props.cluster_id,
        lat,
        lng,
        // Prefer aggregated unit count so multi-unit leaves aren't under-counted.
        count: props.units ?? props.point_count,
        hot: (props.hot ?? 0) > 0,
      })
    } else {
      const leaf = props as PinFeatureProps
      out.push({
        kind: 'leaf',
        propertyId: leaf.propertyId,
        propertyIds: leaf.propertyIds ?? [leaf.propertyId],
        count: leaf.count ?? 1,
        lat,
        lng,
        label: leaf.label,
        hasActiveBiddles: leaf.hasActiveBiddles,
      })
    }
  }
  return out
}
